import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { tokenReceived, userReceived, loggedOut } from "@/store/auth";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import authConfig from "@/config/auth.ts";

import type { RootState } from "@/store";

type TokenResponse = {
  accessToken: string;
  refreshToken: string;
};

const baseQuery = fetchBaseQuery({
  baseUrl: "/api/",
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem(authConfig.storageTokenKeyName);
    if (token) headers.set("authorization", `Bearer ${token}`);
    // Request JSON-LD format for Hydra collection responses
    if (!headers.has("Accept")) {
      headers.set("Accept", "application/ld+json");
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);

  if (result.error && result?.error.status === 401) {
    // Try to refresh the token
    const refreshToken = (api.getState() as RootState).auth.refreshToken;

    if (refreshToken) {
      const refreshResult = await baseQuery(
        {
          url: "/auth/refresh",
          method: "POST",
          body: { refreshToken },
        },
        api,
        extraOptions
      );

      if (refreshResult.data) {
        // Store the new tokens
        api.dispatch(tokenReceived(refreshResult.data as TokenResponse));
        // Retry the original query
        result = await baseQuery(args, api, extraOptions);
      } else {
        // Refresh failed, logout
        api.dispatch(loggedOut());
      }
    } else {
      api.dispatch(loggedOut());
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "Studios",
    "StudioModels",
    "Applications",
    "ApplicationNotes",
    "ApplicationDocuments",
    "NoteTags",
    "Contacts",
    "EmailTemplates",
    "Settings",
    "Dashboard",
    "Users",
  ],
  endpoints: (builder) => ({
    deleteElement: builder.mutation<void, string>({
      query: (endpoint) => ({
        url: endpoint,
        method: "DELETE",
      }),
    }),
  }),
});

export const { useDeleteElementMutation } = api;
